import { toNumber } from '../../utils/numberUtils.js'
import { monthKey } from '../../utils/dateUtils.js'
import RULES_CONFIG from './rules.config.js'
import { calcularProrrateoReciproco } from './formulas/prorrateador.js'
import { resolverCriterioDocenteMixto } from './formulas/prorrateador.js'
import { evaluarRegularidadPension } from './formulas/formulaPension.js'

function toMonthIndex(value) {
  const txt = String(value || '').trim()
  const iso = txt.match(/^(\d{4})-(\d{2})/)
  if (iso) return Number(iso[1]) * 12 + Number(iso[2])
  const slash = txt.match(/^(\d{2})\/(\d{4})$/)
  if (slash) return Number(slash[2]) * 12 + Number(slash[1])
  const d = new Date(txt)
  if (Number.isNaN(d.getTime())) return null
  return d.getFullYear() * 12 + (d.getMonth() + 1)
}

function detectSpecialRegime(expediente = {}) {
  if (expediente?.esDocente === true) {
    return { isSpecial: true, key: 'DOCENTE', label: 'Docente' }
  }
  const bag = [
    expediente?.regimen,
    expediente?.rubro,
    expediente?.actividad,
    expediente?.convenio,
    expediente?.observaciones,
  ].join(' ').toLowerCase()

  if (/docente/.test(bag)) {
    return { isSpecial: true, key: 'DOCENTE', label: 'Docente' }
  }
  if (/construccion|construcción|uocra/.test(bag)) {
    return { isSpecial: true, key: 'CONSTRUCCION', label: 'Construccion' }
  }
  return { isSpecial: false, key: 'LEY_24241', label: 'General' }
}

function detectSimultaneity(rows = []) {
  const byPeriod = new Map()
  rows.forEach((r) => {
    const key = monthKey(r.periodoKey || r.periodoISO || r.periodo || '')
    if (!key) return
    const cuit = String(r.cuit || '').trim() || 'SIN_CUIT'
    const bucket = byPeriod.get(key) || []
    bucket.push(cuit)
    byPeriod.set(key, bucket)
  })

  let duplicateCount = 0
  byPeriod.forEach((cuits) => {
    const uniq = new Set(cuits)
    if (uniq.size < cuits.length) duplicateCount += 1
  })

  return {
    hasSimultaneity: duplicateCount > 0,
    periodsWithDuplicateCuit: duplicateCount,
  }
}

function detectScenario(expediente, rows, special) {
  const pensionType = String(expediente?.datosPension?.tipoPension || '').toUpperCase()
  if (pensionType === 'DERIVADA') return 'PENSION_DERIVADA'
  if (pensionType === 'DIRECTA') return 'PENSION_DIRECTA'
  if (special?.key === 'DOCENTE') {
    const mixedDocente = rows.some((r) => {
      const tag = String(r.tipoActividad || r.tipo || '').toLowerCase()
      return tag.includes('auto') || tag.includes('mono') || tag.includes('autonom')
    })
    return mixedDocente ? 'DOCENTE_MIXTO' : 'DOCENTE_PURO'
  }
  if (special?.isSpecial) return 'ESPECIAL_REGIMEN'
  const mixed = rows.some((r) => {
    const tag = String(r.tipoActividad || r.tipo || '').toLowerCase()
    return tag.includes('auto') || tag.includes('mono') || tag.includes('autonom')
  })
  return mixed ? 'GENERAL_MIXTO' : 'GENERAL_DEPENDENCIA'
}

function diasComputablesRow(r = {}) {
  const candidates = [r?.diasComputables, r?.diasOrigen, r?.dias]
  for (const candidate of candidates) {
    const n = Number(candidate)
    if (Number.isFinite(n) && Number.isInteger(n) && n > 0) return n
  }
  return 30
}

export default function strategicEvaluator(expediente = {}, remuneracionesSanitizadas = []) {
  const rows = Array.isArray(remuneracionesSanitizadas) ? remuneracionesSanitizadas : []
  const aporteRows = rows.filter((r) => toNumber(r.monto || r.remImpSS || r.montoOriginal) > 0)
  const aporteDias = aporteRows.reduce((acc, r) => acc + diasComputablesRow(r), 0)
  const aporteMeses = aporteDias / 30
  const aporteAnios = aporteDias / 365
  const needsSubsanacion = aporteAnios < toNumber(RULES_CONFIG?.subsanacion?.objetivoAportesAnios || 30)

  const ceseReal = expediente?.fechaCese || ''
  const ceseIdx = toMonthIndex(ceseReal)
  const now = new Date()
  const hoyIdx = now.getFullYear() * 12 + (now.getMonth() + 1)
  const diffMesesCeseHoy = ceseIdx ? Math.abs(hoyIdx - ceseIdx) : 0
  const requierePuenteMovilidad = diffMesesCeseHoy > 2

  const special = detectSpecialRegime(expediente)
  const simult = detectSimultaneity(rows)
  const prorrateo = calcularProrrateoReciproco(expediente, rows)
  const requiresValidation = rows.some((r) => Boolean(r.requiresValidation || r.requiereRevision))
  const scenarioKey = detectScenario(expediente, rows, special)
  const scenario = RULES_CONFIG?.scenarioMatrix?.[scenarioKey] || RULES_CONFIG?.scenarioMatrix?.GENERAL_DEPENDENCIA || {}
  const criterioDocenteMixto = scenarioKey === 'DOCENTE_MIXTO'
    ? resolverCriterioDocenteMixto(expediente, rows)
    : null
  const criterioRegularidad = scenarioKey === 'PENSION_DIRECTA'
    ? (() => {
      const reg = evaluarRegularidadPension(rows, expediente?.datosPension?.fechaFallecimiento)
      const irregularFactor = Number(scenario?.tasaSustitucionIrregular || 0.7)
      return {
        mesesAportados36: Number(reg?.m36 || 0),
        mesesAportados60: Number(reg?.m60 || 0),
        condicionLegal: String(reg?.categoria || 'SIN_DERECHO'),
        factorQuita: reg?.categoria === 'IRREGULAR_CON_DERECHO' ? irregularFactor : Number(reg?.factorQuita || 1),
      }
    })()
    : null

  const computedRules = {
    ...RULES_CONFIG,
    caseMeta: {
      aporteMeses,
      aporteAnios: Number(aporteAnios.toFixed(2)),
      needsSubsanacion,
      requiresTareasCuidado: needsSubsanacion,
      requiresMoratoria: needsSubsanacion,
      diffMesesCeseHoy,
      requierePuenteMovilidad,
      specialRegimeDetected: special.isSpecial,
      specialRegimeKey: special.key,
      specialRegimeLabel: special.label,
      hasSimultaneity: simult.hasSimultaneity,
      periodsWithDuplicateCuit: simult.periodsWithDuplicateCuit,
      requiresValidation,
      puenteRequerido: requierePuenteMovilidad,
      subsanacionActive: needsSubsanacion,
      scenarioKey,
      scenarioLabel: String(scenario.label || scenarioKey),
      prorrateoReciproco: prorrateo,
      criterioOtorgamientoDocenteMixto: criterioDocenteMixto,
      criterioRegularidad,
      exentoTopeArt9: special.key === 'DOCENTE',
    },
    subsanacion: {
      ...RULES_CONFIG.subsanacion,
      decreto_475_2021_tareas_cuidado: {
        ...RULES_CONFIG.subsanacion.decreto_475_2021_tareas_cuidado,
        activeByCase: needsSubsanacion && scenario.habilitaTareasCuidado !== false,
      },
      ley_27705_moratoria: {
        ...RULES_CONFIG.subsanacion.ley_27705_moratoria,
        activeByCase: needsSubsanacion && scenario.habilitaMoratoria !== false,
      },
    },
    movilidad: {
      ...RULES_CONFIG.movilidad,
      puenteRequerido: requierePuenteMovilidad,
      puenteMesesMinimos: 2,
    },
    protocol: {
      ...RULES_CONFIG.protocol,
      divisorPolicy: String(scenario.divisorPolicy || RULES_CONFIG?.protocol?.divisorPolicy || 'legal_120'),
      sumarSimultaneidad: RULES_CONFIG.protocol.sumarSimultaneidad || simult.hasSimultaneity,
      simultaneidadDetectada: simult.hasSimultaneity,
    },
    scenarioApplied: {
      key: scenarioKey,
      ...scenario,
    },
    perfiles: special.isSpecial
      ? {
          ...RULES_CONFIG.perfiles,
          selected: {
            type: 'especial',
            regimen: special.key,
            accion: 'override_jubilacion_ordinaria',
          },
        }
      : {
          ...RULES_CONFIG.perfiles,
          selected: {
            type: 'general',
            regimen: 'LEY_24241',
            accion: 'flujo_general_v4',
          },
        },
  }

  return computedRules
}
